"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.removeBlocksAndCleanReferences = exports.updateBlocksObject = exports.removeIdFromChildrenIds = exports.cleanRemovedChildrenIds = exports.getAllChildrenIds = exports.getAllBlockIdsToDelete = exports.getChildrenIdsFromBlock = void 0;
/**
 * Extracts the children ids from a block based on its type. For ColumnsContainer blocks, the columnIndex
 * parameter is required to get the correct column's children.
 */
const getChildrenIdsFromBlock = (block, columnIndex) => {
    if (block.type === "EmailLayout") {
        return (block.data.childrenIds || []);
    }
    if (block.type === "Container") {
        return (block.data.props?.childrenIds || []);
    }
    if (block.type === "ColumnsContainer") {
        if (columnIndex === undefined) {
            return [];
        }
        return (block.data.props?.columns?.[columnIndex]?.childrenIds || []);
    }
    return [];
};
exports.getChildrenIdsFromBlock = getChildrenIdsFromBlock;
/**
 * Recursively collects all block ids that need to be deleted from an email-builder document, including the
 * block itself and all nested children.
 */
const getAllBlockIdsToDelete = (blockId, blocks) => {
    const block = blocks[blockId];
    if (!block) {
        return [blockId];
    }
    const ids = [blockId];
    if (block.type === "Container") {
        const childrenIds = block.data.props?.childrenIds || [];
        for (const childId of childrenIds) {
            ids.push(...(0, exports.getAllBlockIdsToDelete)(childId, blocks));
        }
    }
    else if (block.type === "ColumnsContainer") {
        const columns = block.data.props?.columns || [];
        for (const column of columns) {
            for (const childId of column.childrenIds || []) {
                ids.push(...(0, exports.getAllBlockIdsToDelete)(childId, blocks));
            }
        }
    }
    return ids;
};
exports.getAllBlockIdsToDelete = getAllBlockIdsToDelete;
const getAllChildrenIds = (block) => {
    if (block.type === "ColumnsContainer") {
        const columns = block.data.props?.columns ?? [];
        return columns.flatMap((_, columnIndex) => (0, exports.getChildrenIdsFromBlock)(block, columnIndex));
    }
    return (0, exports.getChildrenIdsFromBlock)(block);
};
exports.getAllChildrenIds = getAllChildrenIds;
const cleanRemovedChildrenIds = (block, removedIds) => {
    if (block.type === "ColumnsContainer") {
        const columns = block.data.props?.columns ?? [];
        return columns.reduce((current, _, columnIndex) => {
            const filtered = (0, exports.getChildrenIdsFromBlock)(current, columnIndex).filter((id) => !removedIds.has(id));
            return (0, exports.updateBlocksObject)(current, filtered, columnIndex);
        }, block);
    }
    const filtered = (0, exports.getChildrenIdsFromBlock)(block).filter((id) => !removedIds.has(id));
    return (0, exports.updateBlocksObject)(block, filtered);
};
exports.cleanRemovedChildrenIds = cleanRemovedChildrenIds;
/**
 * Returns the parent's childrenIds with `blockId` removed. For a ColumnsContainer, a specific column can be
 * targeted via `columnIndex`; when omitted, the id is removed from every column (flattened result).
 */
const removeIdFromChildrenIds = (insideBlock, blockId, columnIndex) => {
    if (insideBlock.type === "EmailLayout") {
        const emailLayoutBlock = insideBlock;
        return (emailLayoutBlock.data.childrenIds || []).filter((id) => id !== blockId);
    }
    else if (insideBlock.type === "Container") {
        const containerBlock = insideBlock;
        return (containerBlock.data.props?.childrenIds || []).filter((id) => id !== blockId);
    }
    else if (insideBlock.type === "ColumnsContainer") {
        const columnsContainerBlock = insideBlock;
        const columns = columnsContainerBlock.data.props?.columns || [];
        if (columnIndex !== undefined) {
            const column = columns[columnIndex];
            const childrenIds = column?.childrenIds || [];
            return childrenIds.filter((id) => id !== blockId);
        }
        return columns.flatMap((column) => {
            const childrenIds = column.childrenIds || [];
            return childrenIds.filter((id) => id !== blockId);
        });
    }
    return [];
};
exports.removeIdFromChildrenIds = removeIdFromChildrenIds;
/**
 * Returns a copy of `parentBlock` with its childrenIds replaced by `newChildrenIds`, writing to the correct
 * location for the block type. For a ColumnsContainer, `columnIndex` selects which column to overwrite.
 */
const updateBlocksObject = (parentBlock, newChildrenIds, columnIndex) => {
    if (parentBlock.type === "EmailLayout") {
        const emailLayoutBlock = parentBlock;
        return {
            ...emailLayoutBlock,
            data: {
                ...emailLayoutBlock.data,
                childrenIds: newChildrenIds,
            },
        };
    }
    if (parentBlock.type === "Container") {
        const containerBlock = parentBlock;
        return {
            ...containerBlock,
            data: {
                ...containerBlock.data,
                props: {
                    ...containerBlock.data.props,
                    childrenIds: newChildrenIds,
                },
            },
        };
    }
    if (parentBlock.type === "ColumnsContainer") {
        const columnsContainerBlock = parentBlock;
        const columns = columnsContainerBlock.data.props?.columns || [];
        return {
            ...columnsContainerBlock,
            data: {
                ...columnsContainerBlock.data,
                props: {
                    ...columnsContainerBlock.data.props,
                    columns: columns.map((column, index) => {
                        if (index === columnIndex) {
                            return { childrenIds: newChildrenIds };
                        }
                        return column;
                    }),
                },
            },
        };
    }
    return parentBlock;
};
exports.updateBlocksObject = updateBlocksObject;
const removeBlocksAndCleanReferences = (blocks, blockIds) => {
    const removedIds = new Set(blockIds);
    for (const id of removedIds) {
        delete blocks[id];
    }
    for (const [id, block] of Object.entries(blocks)) {
        blocks[id] = (0, exports.cleanRemovedChildrenIds)(block, removedIds);
    }
};
exports.removeBlocksAndCleanReferences = removeBlocksAndCleanReferences;
